"use client"

import styled from "@emotion/styled"
import { Box, Button, TextField, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import InvestChangeArrow from "ui/common/bit/InvestChangeArrow"
import { colors, sizes } from "ui/theme/theme"
import { InvestmentFormProps } from "./types"

const StyledTextField = styled(TextField)`
  min-width: 200px;
  width: 100%;
  flex-grow: 1;
  height: 42px;

  & .MuiOutlinedInput-root {
    border-radius: 8px;
    border: none;
    box-shadow: 0px 0px 14px 10px rgba(30, 50, 90, 0.14);
    height: inherit;
  }
  & .MuiInputBase-input {
    padding: 6.5px 14px;
  }
`

const StyledButton = styled(Button)`
  display: flex;
  flex-grow: 1;
  background-color: ${colors.red};
  color: ${colors.white};
  padding: ${sizes.baseSingle} ${sizes.baseDouble};
  border-radius: 8px;
  white-space: nowrap;
  height: 42px;
  box-shadow: 0px 0px 14px 10px rgba(30, 50, 90, 0.14);
  &:hover {
    background-color: ${colors.red};
  }
  && {
    font-size: 14px;
  }
`

const StyledFormContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${sizes.baseDouble};
  width: 100%;
  margin-bottom: ${sizes.baseTriple};
`

const StyledResultsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.baseSingle};
  margin-bottom: ${sizes.baseTriple};
`

const StyledInitialAmountTypography = styled(Typography)`
  color: ${colors.black};
  font-size: 44px;
  font-weight: 700;
  line-height: 1.2;
`

const StyledEstimatedValueContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${sizes.baseSingle};
`

const StyledEstimatedValueTypography = styled(Typography)`
  color: ${colors.red};
  font-weight: bold;
  font-size: 16px;
  span {
    color: ${colors.black};
  }
`

const StyledProfitContainer = styled(Box)`
  display: flex;
  align-items: center;
  border-radius: 4px;
  border: 0.741px solid ${colors.bluishGray};
  background: rgba(57, 90, 161, 0.1);
  padding: 4px 2px 4px ${sizes.baseSingle};
  font-size: 18px;
  font-weight: 700;
  color: ${colors.purplishBlue};
  gap: 2px;
`

const StyledDiv = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${sizes.baseSingle};
`

const InvestmentForm = ({
  investment,
  setInvestment,
  submitted,
  estimatedValue,
  profitPercentage,
  formatNumber,
  handleSubmit,
}: InvestmentFormProps) => {
  const t = useTranslations("InvestSimulator")
  const formatInvestment = (value: number) => {
    if (value === null || value === undefined || isNaN(value)) return ""
    return value.toLocaleString("he-IL")
  }

  return (
    <>
      {!submitted ? (
        <StyledFormContainer>
          <StyledTextField
            type="text"
            value={!investment ? "" : `₪${formatInvestment(investment || 0)}`}
            variant="outlined"
            onChange={(e) => {
              // Remove all non-digit characters
              const raw = e.target.value.replace(/[^\d]/g, "")
              setInvestment(Number(raw))
            }}
            fullWidth
            inputProps={{
              dir: "rtl",
              style: { textAlign: "right" },
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
          <StyledButton onClick={handleSubmit} variant="contained" fullWidth>
            {t("buttonLabel")}
          </StyledButton>
        </StyledFormContainer>
      ) : (
        <StyledResultsContainer>
          <StyledDiv>
            {estimatedValue && (
              <StyledInitialAmountTypography variant="h6">
                ₪{formatNumber(parseInt(estimatedValue.toFixed(0)))}
              </StyledInitialAmountTypography>
            )}
            {profitPercentage !== null && (
              <StyledProfitContainer
                style={profitPercentage < 0 ? { color: colors.red } : {}}
              >
                {profitPercentage.toFixed(2)}%
                <InvestChangeArrow
                  direction={profitPercentage >= 0 ? "up" : "down"}
                  color={profitPercentage < 0 ? colors.red : "#395AA1"}
                />
              </StyledProfitContainer>
            )}
          </StyledDiv>
          {estimatedValue && (
            <StyledEstimatedValueContainer>
              <StyledEstimatedValueTypography>
                {t("initialAmountLabel")}{" "}
                <span>₪{formatNumber(investment || 0)}</span>
              </StyledEstimatedValueTypography>
            </StyledEstimatedValueContainer>
          )}
        </StyledResultsContainer>
      )}
    </>
  )
}

export default InvestmentForm
